import api from './api';

// Device types
export interface Device {
  id: number;
  device_id: string;
  name: string;
  status: 'online' | 'offline' | 'inactive';
  deployed: boolean;
  deployed_at?: string;
  deployment_batch?: string;
  company_id: number;
  company_name?: string;
  gateway_id?: number;
  last_seen?: string;
  last_seen_local?: string | null;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
  // Hardware identity — board catalog row (joined as board_*) plus per-unit
  // provisioning facts. manufacturer/board are the denormalized copies kept
  // for the frozen v1 payloads; prefer the board_* columns when present.
  board_id?: number | null;
  board_manufacturer?: string | null;
  board_name?: string | null;
  board_display_name?: string | null;
  board_capabilities?: string[] | null;
  board_features?: Record<string, boolean> | null;
  manufacturer?: string | null;
  board?: string | null;
  hardware_version?: string | null;
  num_doors?: number;
  power_type?: 'battery' | 'plugin';
  battery_chemistry?: 'alkaline' | 'lithium' | 'li-ion' | null;
  reader_protocol?: 'osdp' | 'wiegand' | null;
  reader_technology?: 'prox' | 'smartcard' | 'nfc' | 'ble' | 'multi' | null;
}

// Curated device-state snapshot returned by GET /devices/:id/state — the REST
// equivalent of the MQTT /state topic. Replaces the deprecated /status read.
export interface DeviceState {
  deviceId: string;
  name: string | null;
  status: 'online' | 'offline' | 'inactive';
  lastSeen: string | null;
  lastSeenLocal: string | null;
  lock: { state: number | string | null; override: number | null };
  power: { mode: number | string | null; deepSleepDuration: number | null; current: number | null };
  // `battery` is the coarse firmware bucket ('low'/'medium'/'high'/'dead');
  // `batteryPct` is the coulomb-counter percentage (may be null on devices
  // without the counter). Both come from buildState on GET /devices/:id/state.
  battery: string | number | null;
  batteryPct: number | null;
  firmware: string | null;
  osdpStage: number | null;
  counts: {
    credentials: number | null;
    shifts: number | null;
    holidays: number | null;
    doorShifts: number | null;
  };
  connectivity: { carrier: string | null; signal: number | null };
  config: { cardType: number | null; latchInterval: number | null };
}

// Cellular signal quality bands (dBm), shared by the device list and detail
// views so the tag means the same thing everywhere. Anchors: −75 Great /
// −90 Good / −110 Poor. ≥−75 and −90…−75 both read as healthy (green); below
// −90 is Poor (red). Signal originates from the device's metadata.state.signal
// (mirrored on the /state snapshot as connectivity.signal).
export type SignalTier = { label: string; variant: 'positive' | 'critical' | 'neutral' };

export function signalQuality(dbm?: number | null): SignalTier {
  if (dbm == null) return { label: '—', variant: 'neutral' };
  if (dbm >= -75) return { label: 'Great', variant: 'positive' };
  if (dbm >= -90) return { label: 'Good', variant: 'positive' };
  return { label: 'Poor', variant: 'critical' };
}

export interface DeviceListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  // Accepts boolean or string ("true"/"false") — the Devices.tsx filter
  // dropdown stores its value as a string, and axios serializes both shapes
  // identically. The API coerces via `deployed === 'true'` regardless.
  deployed?: boolean | string;
  company_id?: number;
  companyId?: string;
  gateway_id?: number;
  firmware?: string;
  mode?: string;
  sort?: 'last_seen' | 'created_at';
  order?: 'asc' | 'desc';
}

export interface DeviceListResponse {
  devices: Device[];
  total: number;
  page: number;
  pages: number;
}

export interface CreateDeviceData {
  device_id: string;
  name: string;
  company_id: number;
  gateway_id?: number;
  metadata?: Record<string, any>;
}

export interface UpdateDeviceData {
  name?: string;
  metadata?: Record<string, any>;
  gateway_id?: number;
  board_id?: number;
  hardware_version?: string | null;
  num_doors?: number;
  power_type?: 'battery' | 'plugin';
  battery_chemistry?: 'alkaline' | 'lithium' | 'li-ion' | null;
  reader_protocol?: 'osdp' | 'wiegand' | null;
  reader_technology?: 'prox' | 'smartcard' | 'nfc' | 'ble' | 'multi' | null;
  // Software-provider assignment (super admin only; null = unassigned pool).
  company_id?: string | null;
}

export interface SendCommandPayload {
  command: string;
  payload?: Record<string, any>;
}

export interface CommandResponse {
  success: boolean;
  message: string;
  command_id?: string;
}

export interface SyncResponse {
  synced: number;
  message: string;
}

export interface BatchDeployResponse {
  success: boolean;
  updated: number;
  message: string;
}

export interface FleetStats {
  total: number;
  online: number;
  offline: number;
  inactive: number;
  deployed: number;
  undeployed: number;
}

export interface CompanyFleetStats {
  company_id: number;
  company_name: string;
  stats: FleetStats;
}

export async function getDevices(params: DeviceListParams = {}): Promise<DeviceListResponse> {
  // Filter out empty/undefined params
  const cleanParams: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      cleanParams[key] = value;
    }
  }
  const response = await api.get<DeviceListResponse>('/devices', { params: cleanParams });
  return response.data;
}

export async function getDevice(id: number): Promise<Device> {
  const response = await api.get<Device>(`/devices/${id}`);
  return response.data;
}

export async function getDeviceState(id: number | string): Promise<DeviceState> {
  const response = await api.get<DeviceState>(`/devices/${id}/state`);
  return response.data;
}

export async function sendCommand(id: number, command: string, payload?: Record<string, any>): Promise<CommandResponse> {
  const response = await api.post<CommandResponse>(`/devices/${id}/commands`, { command, payload });
  return response.data;
}

export async function createDevice(data: CreateDeviceData): Promise<Device> {
  const response = await api.post<Device>('/devices', data);
  return response.data;
}

export async function syncDevices(): Promise<SyncResponse> {
  const response = await api.post<SyncResponse>('/devices/sync');
  return response.data;
}

export async function syncDeviceStatuses(): Promise<SyncResponse> {
  const response = await api.post<SyncResponse>('/devices/sync/status');
  return response.data;
}

// Deploy and undeploy take the device_id string (the nRF Cloud identifier),
// not the row's primary key — same convention as every other device route.
export async function deployDevice(deviceId: string): Promise<Device> {
  const response = await api.post<Device>(`/devices/${deviceId}/deploy`);
  return response.data;
}

export async function undeployDevice(deviceId: string): Promise<Device> {
  const response = await api.post<Device>(`/devices/${deviceId}/undeploy`);
  return response.data;
}

export async function batchDeployDevices(deviceIds: string[], deployed = true, deploymentBatch?: string): Promise<BatchDeployResponse> {
  const response = await api.post<BatchDeployResponse>('/devices/deploy/batch', { deviceIds, deployed, deploymentBatch });
  return response.data;
}

export async function getNextBatchNumber(): Promise<string> {
  const response = await api.get<{ batchNumber: string }>('/devices/deploy/next-batch');
  return response.data.batchNumber;
}

export async function getFleetStats(): Promise<FleetStats> {
  const response = await api.get<FleetStats>('/devices/fleet/stats');
  return response.data;
}

export async function getFirmwareVersions(): Promise<string[]> {
  const response = await api.get<{ versions: string[] }>('/devices/firmware-versions');
  return response.data.versions;
}

// The API returns { companies: [{ companyId, companyName, total, online, offline,
// deployed }] } — flat fields wrapped in an object. Unwrap and reshape to the
// nested CompanyFleetStats the dashboard consumes.
interface CompanyFleetStatsRow {
  companyId: number;
  companyName: string;
  total: number;
  online: number;
  offline: number;
  deployed: number;
}

export async function getFleetStatsByCompany(): Promise<CompanyFleetStats[]> {
  const response = await api.get<{ companies: CompanyFleetStatsRow[] }>('/devices/fleet/stats/by-company');
  return (response.data.companies || []).map(row => ({
    company_id: row.companyId,
    company_name: row.companyName,
    stats: {
      total: row.total,
      online: row.online,
      offline: row.offline,
      deployed: row.deployed,
      inactive: 0,
      undeployed: row.total - row.deployed,
    },
  }));
}

/**
 * Update device details
 * @requires Company Admin role
 */
// Takes the device_id string, same as the detail-page URL and deploy calls.
export async function updateDevice(id: string, data: UpdateDeviceData): Promise<Device> {
  const response = await api.patch<Device>(`/devices/${id}`, data);
  return response.data;
}

/**
 * Delete a device
 * @requires Super Admin role
 */
export async function deleteDevice(id: number): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/devices/${id}`);
  return response.data;
}

// Device Events
export interface DeviceEvent {
  id: string;
  device_id: string;
  event_type: string;
  event_category: string;
  event_data: Record<string, any>;
  metadata: Record<string, any>;
  severity: string;
  created_at: string;
  device_ts_local?: string | null;
}

export interface DeviceEventsResponse {
  success: boolean;
  data: DeviceEvent[];
  pagination: { total: number; limit: number; offset: number };
}

export interface QueuedCommand {
  id: number;
  device_id: string;
  company_id: number | null;
  command_type: string;
  payload: Record<string, any>;
  options: Record<string, any>;
  priority: number;
  status: 'pending' | 'processing' | 'sent' | 'failed' | 'cancelled';
  attempts: number;
  max_attempts: number;
  error_message: string | null;
  created_at: string;
  sent_at: string | null;
  last_attempt_at: string | null;
}

export interface DeviceQueueResponse {
  success: boolean;
  commands: QueuedCommand[];
}

export async function getDeviceQueue(
  id: number,
  params?: { since?: string; limit?: number },
): Promise<DeviceQueueResponse> {
  const cleanParams: Record<string, any> = {};
  if (params?.since) cleanParams.since = params.since;
  if (params?.limit) cleanParams.limit = params.limit;
  const response = await api.get<DeviceQueueResponse>(`/devices/${id}/queue`, { params: cleanParams });
  return response.data;
}

export interface CancelCommandResponse {
  success: boolean;
  command?: QueuedCommand;
}

/**
 * Cancel a pending command in the device's queue.
 * @requires Company Admin role
 */
export async function cancelDeviceCommand(deviceId: number, commandId: number): Promise<CancelCommandResponse> {
  const response = await api.delete<CancelCommandResponse>(`/devices/${deviceId}/queue/${commandId}`);
  return response.data;
}

export type TelemetryWindow = '24h' | '7d' | '30d';

export interface TelemetrySample {
  id: number;
  sampled_at: string;
  sampled_at_local: string | null;
  battery_level: number | null;
  battery_pct: number | null;
  signal_dbm: number | null;
  carrier: string | null;
  diag_hold: boolean | null;
  credential_count: number | null;
  shift_count: number | null;
  holiday_count: number | null;
  door_shift_count: number | null;
  wake_up_count: number | null;
  sleep_duration_ms: number | null;
  lock_state: string | null;
  power_state: string | null;
  firmware_version: string | null;
}

export interface TelemetryResponse {
  window: TelemetryWindow;
  since: string;
  samples: TelemetrySample[];
}

export async function getDeviceTelemetry(id: number | string, window: TelemetryWindow = '24h'): Promise<TelemetryResponse> {
  const response = await api.get<TelemetryResponse>(`/devices/${id}/telemetry`, { params: { window } });
  return response.data;
}

export async function getDeviceEvents(deviceId: string, params?: {
  limit?: number;
  offset?: number;
  category?: string;
  severity?: string;
  startDate?: string;
}): Promise<DeviceEventsResponse> {
  const cleanParams: Record<string, any> = {};
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') {
        cleanParams[key] = value;
      }
    }
  }
  const response = await api.get<DeviceEventsResponse>(`/device-events/device/${deviceId}`, { params: cleanParams });
  return response.data;
}

export default {
  getDevices,
  getDevice,
  getDeviceState,
  sendCommand,
  createDevice,
  syncDevices,
  syncDeviceStatuses,
  deployDevice,
  undeployDevice,
  batchDeployDevices,
  getNextBatchNumber,
  getFleetStats,
  getFleetStatsByCompany,
  getFirmwareVersions,
  updateDevice,
  deleteDevice,
  getDeviceEvents,
  getDeviceQueue,
};
